import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Avatar,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AppsIcon from '@mui/icons-material/Apps';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateBusinessDialog from '@/components/CreateBusinessDialog';
import { useBusinessStore } from '@/store/businessStore';
import { businessesApi } from '@/api/businesses';
import { projectColor, initialsOf } from '@/theme/tokens';
import { getBusinessIcon } from '@/theme/businessIcons';

export const RAIL_COLLAPSED = 76;
export const RAIL_EXPANDED = 232;

/*
 * Every piece of text in here sets its colour explicitly.
 *
 * The rail used to rely on `color: 'common.white'` inherited from the
 * container, which left the labels rendering in the theme's dark body colour —
 * navy text on a navy background, all but unreadable. Naming the colour at each
 * site costs a few characters and removes a whole class of bug.
 */
const TEXT = {
  bright: '#FFFFFF',
  normal: 'rgba(255,255,255,.78)',
  dim: 'rgba(255,255,255,.52)',
  faint: 'rgba(255,255,255,.38)',
};

const SURFACE = {
  active: 'rgba(255,255,255,.14)',
  hover: 'rgba(255,255,255,.07)',
  line: 'rgba(255,255,255,.10)',
};

export default function IconRail({ expanded, onToggle, onNavigate }) {
  const { currentBusinessId, setCurrentBusiness } = useBusinessStore();
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);

  const { data } = useQuery({
    // One key for the business list — see hooks/useBusinesses. A suffix here
    // made this a SEPARATE cache of identical data and a third duplicate
    // request on every page load.
    queryKey: ['businesses'],
    queryFn: () => businessesApi.list(),
  });
  const businesses = data?.data || data || [];

  const width = expanded ? RAIL_EXPANDED : RAIL_COLLAPSED;

  const selectProject = (b) => {
    setCurrentBusiness(b.id);
    onNavigate?.();
  };

  /**
   * One row, used for every entry so they can't drift apart.
   *
   * Active state is a soft pill plus a short bar on the left edge, rather than
   * the old double ring drawn around the avatar. The ring had to be redrawn in
   * the exact background colour to work, which meant it broke the moment the
   * gradient behind it changed — and it shouted louder than a selected item
   * needs to.
   */
  const Row = ({ active, onClick, avatar, label, muted = false, tooltip }) => {
    if (!expanded) {
      return (
        <Tooltip title={tooltip || label} placement="right" arrow>
          <Box
            onClick={onClick}
            sx={{
              position: 'relative', cursor: 'pointer', lineHeight: 0,
              transition: 'transform 160ms ease',
              '&:hover': { transform: 'translateY(-1px)' },
            }}
          >
            {/* Collapsed has no pill to carry the state, so the avatar wears a
                ring instead — a single soft one, not the old two-tone. */}
            <Box
              sx={{
                borderRadius: '50%',
                p: '2px',
                border: `2px solid ${active ? TEXT.bright : 'transparent'}`,
                transition: 'border-color 160ms ease',
              }}
            >
              {avatar}
            </Box>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        onClick={onClick}
        sx={{
          position: 'relative',
          px: 1, py: 0.85,
          borderRadius: 2.5,
          cursor: 'pointer',
          bgcolor: active ? SURFACE.active : 'transparent',
          transition: 'background 160ms ease',
          '&:hover': { bgcolor: active ? SURFACE.active : SURFACE.hover },
          '&:hover .railLabel': { color: TEXT.bright },
          // Left edge indicator for the selected workspace.
          '&::before': active
            ? {
                content: '""',
                position: 'absolute', left: -12, top: '50%',
                transform: 'translateY(-50%)',
                width: 3, height: 22, borderRadius: 999,
                bgcolor: TEXT.bright,
              }
            : undefined,
        }}
      >
        {avatar}
        <Typography
          className="railLabel"
          noWrap
          sx={{
            fontSize: 13.5,
            fontWeight: active ? 700 : 500,
            color: active ? TEXT.bright : muted ? TEXT.dim : TEXT.normal,
            transition: 'color 160ms ease',
          }}
        >
          {label}
        </Typography>
      </Stack>
    );
  };

  const avatarSx = (active) => ({
    width: 38,
    height: 38,
    fontWeight: 700,
    fontSize: 14,
    color: '#fff',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'box-shadow 160ms ease',
    boxShadow: active ? '0 6px 16px -6px rgba(0,0,0,.65)' : 'none',
  });

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: TEXT.bright,
        background: (t) =>
          `linear-gradient(165deg, ${t.tokens.navy} 0%, ${t.tokens.navy2} 58%, #2A3878 100%)`,
        transition: 'width 220ms ease',
        overflow: 'hidden',
      }}
    >
      {/*
        No brand mark in this rail.

        It used to sit here, but collapsed the lockup was squeezed into a 76px
        column and rendered as an unreadable smudge, and expanded it repeated
        the logo the light sidebar already shows. One mark per screen; this rail
        starts straight into the workspaces it exists for.
      */}

      {/* Workspaces */}
      <Stack
        spacing={expanded ? 0.5 : 1.5}
        sx={{
          flex: 1,
          overflowY: 'auto',
          alignItems: expanded ? 'stretch' : 'center',
          px: expanded ? 1.5 : 0,
          pt: 2.5,
          pb: 1,
          '&::-webkit-scrollbar': { width: 0 },
        }}
      >
        {expanded && (
          <Typography
            sx={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: '.14em',
              textTransform: 'uppercase', color: TEXT.faint,
              px: 1, pb: 0.75,
            }}
          >
            Workspaces
          </Typography>
        )}

        {/* All Projects */}
        <Row
          active={!currentBusinessId}
          onClick={() => setCurrentBusiness('')}
          label="All Projects"
          avatar={
            <Avatar sx={{ ...avatarSx(!currentBusinessId), bgcolor: 'rgba(255,255,255,.14)' }}>
              <AppsIcon sx={{ fontSize: 19 }} />
            </Avatar>
          }
        />

        {businesses.map((b) => {
          const active = String(currentBusinessId) === String(b.id);
          const color = b.profile?.color || b.color || b.brand_color || projectColor(b.id || b.name);
          const BizIcon = !b.logo ? getBusinessIcon(b.profile?.icon) : null;
          return (
            <Row
              key={b.id}
              active={active}
              onClick={() => selectProject(b)}
              label={b.name}
              avatar={
                <Avatar src={b.logo || undefined} sx={{ ...avatarSx(active), bgcolor: color }}>
                  {BizIcon ? <BizIcon sx={{ fontSize: 19 }} /> : initialsOf(b.name)}
                </Avatar>
              }
            />
          );
        })}

        {/* Add new business — dashed outline, so it reads as a slot to fill
            rather than as another workspace that already exists. */}
        <Row
          active={false}
          muted
          onClick={() => setAddBusinessOpen(true)}
          label="Add New Business"
          tooltip="Add New Business"
          avatar={
            <Box
              sx={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px dashed rgba(255,255,255,.32)',
                color: TEXT.normal,
                transition: 'all 160ms ease',
                '&:hover': { borderColor: TEXT.bright, color: TEXT.bright, bgcolor: 'rgba(255,255,255,.06)' },
              }}
            >
              <AddIcon sx={{ fontSize: 19 }} />
            </Box>
          }
        />
      </Stack>

      {/* Collapse toggle */}
      <Box
        sx={{
          borderTop: `1px solid ${SURFACE.line}`,
          p: expanded ? 1.5 : 1,
          display: 'flex',
          justifyContent: expanded ? 'flex-start' : 'center',
          flexShrink: 0,
        }}
      >
        {expanded ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={onToggle}
            sx={{
              px: 1, py: 0.75,
              borderRadius: 2,
              cursor: 'pointer',
              color: TEXT.dim,
              width: '100%',
              transition: 'all 160ms ease',
              '&:hover': { bgcolor: SURFACE.hover, color: TEXT.bright },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: 'inherit' }}>
              HIDE MENU
            </Typography>
          </Stack>
        ) : (
          <Tooltip title="Expand" placement="right" arrow>
            <IconButton
              onClick={onToggle}
              sx={{ color: TEXT.dim, '&:hover': { color: TEXT.bright, bgcolor: SURFACE.hover } }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <CreateBusinessDialog open={addBusinessOpen} onClose={() => setAddBusinessOpen(false)} />
    </Box>
  );
}
